import { Song, SongStatusLabel } from 'types/song';
import { routes } from 'app/routing/routesConfig';
import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { useNavigate } from 'react-router-dom';
import { ListItemAction } from 'app/components/ChakraTableList/ListItemAction';
import { SongCoverActionButton } from 'app/components/Song/SongCoverActionButton';
import { SongCover } from 'app/components/Song/SongCover';
import { format } from 'date-fns';
import { dateFormats } from 'app/constants';
import { memo, MouseEvent } from 'react';
import { DragSourceMonitor, useDrag } from 'react-dnd';
import { SONG_DRAG_EVENT_TYPE } from 'app/components/Song/SongList/constants';
import { ContextMenuOption } from 'types/general.types';

interface SongRowProps {
  song: Song;
  options: ContextMenuOption[];
  audioGuid: string;
  isPlaying: boolean;
  handlePlay: (song: Song) => void;
  isAudioLoading: boolean;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
  onNavigated: (guid: string) => void;
  selectedRowsIds: string[];
  onRowSelect: (rowData: { id: string }, shift?: boolean) => void;
}

const SongRowComponent = ({
  audioGuid,
  song,
  isPlaying,
  handlePlay,
  isAudioLoading,
  contextHandler,
  selectedRowsIds,
  onRowSelect,
  onNavigated,
}: SongRowProps) => {
  const shouldShowPlay =
    song._links?.playLatestVersion && song.latestVersionAudio;
  const isSongLoaded = audioGuid === song.latestVersionAudio;
  const isSongPlaying = isPlaying && isSongLoaded;
  const href = routes.song.path.replace(':guid', song.guid);

  const songGuid = song.guid;
  const isSelected = selectedRowsIds.some((id) => id === songGuid);

  const songGuids =
    isSelected && selectedRowsIds.length > 0 ? selectedRowsIds : [songGuid];

  const navigate = useNavigate();
  const [, drag] = useDrag({
    type: SONG_DRAG_EVENT_TYPE,
    item: {
      label: song.title,
      songGuids,
    },
    collect: (monitor: DragSourceMonitor) => {
      return {
        isDragging: monitor.isDragging(),
        opacity: monitor.isDragging() ? 0.4 : 1,
        canDrag:
          monitor.getItemType() === SONG_DRAG_EVENT_TYPE && monitor.canDrag(),
      };
    },
  });

  return (
    <Tr
      zIndex={1}
      position="relative"
      role="row"
      className={isSelected ? 'selected' : ''}
      clickable
      onContextMenu={contextHandler}
      ref={drag}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        if (e.shiftKey) {
          onRowSelect({ id: songGuid }, true);
          e.preventDefault();
        } else if (e.ctrlKey || e.metaKey) {
          onRowSelect({ id: songGuid });
          e.preventDefault();
        } else {
          navigate(href);
          onNavigated(song.guid);
        }
      }}
    >
      <Td isAvatar sx={{ overflow: 'visible' }}>
        {shouldShowPlay ? (
          <ListItemAction onClick={() => handlePlay(song)}>
            <SongCoverActionButton
              isSongPlaying={isSongPlaying}
              status={song.status}
              isLoading={isSongLoaded && isAudioLoading}
            />
          </ListItemAction>
        ) : (
          <SongCover size="medium" status={song.status} />
        )}
      </Td>
      <Td
        data-testid="title"
        sx={{ pl: 0 }}
        color={isSongLoaded || isSongPlaying ? 'P.80' : undefined}
      >
        {song.title}
      </Td>
      <Td data-testid="status">
        {song.status && SongStatusLabel[song.status]}
      </Td>
      <Td data-testid="updated">
        {format(new Date(song.updated.iso), dateFormats.LIST_FORMAT)}
      </Td>
      <Td data-testid="created">
        {format(new Date(song.created.iso), dateFormats.LIST_FORMAT)}
      </Td>
      <Td className="gap" />
      <Td>
        <ContextMenuButton onClick={contextHandler} />
      </Td>
    </Tr>
  );
};

export const SongRow = memo(
  SongRowComponent,
  (prev: SongRowProps, curr: SongRowProps) => {
    const compare = (prev: any, curr: any) =>
      JSON.stringify(prev) === JSON.stringify(curr);

    const compareOptions = (
      prev: SongRowProps['options'],
      curr: SongRowProps['options']
    ) => {
      return (
        prev
          .map((el) => `${el.value}${el.title}${el.isDisabled}`)
          .toString() ===
        curr.map((el) => `${el.value}${el.title}${el.isDisabled}`).toString()
      );
    };

    return (
      prev.onRowSelect === curr.onRowSelect &&
      compare(prev.song.guid, curr.song.guid) &&
      compare(prev.selectedRowsIds, curr.selectedRowsIds) &&
      compare(prev.song._links, curr.song._links) &&
      compare(prev.audioGuid, curr.audioGuid) &&
      compare(prev.isAudioLoading, curr.isAudioLoading) &&
      compare(prev.isPlaying, curr.isPlaying) &&
      compareOptions(prev.options, curr.options)
    );
  }
);
